import React, { FC, useContext } from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import CustomCheckbox from "@/shared/forms/CustomCheckbox";
import CustomInput from "@/shared/forms/CustomInput";
import CustomSelect from "@/shared/forms/CustomSelect";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import CloseIcon from "@mui/icons-material/Close";
import DomainAddOutlinedIcon from "@mui/icons-material/DomainAddOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { Form, Formik } from "formik";

import NewOrganisationModalValidation from "@/components/admin/admin-dashboard/components/NewOrganisationModalValidation";

import { Timezones } from "@/model/Application/Settings/Timezones";
import { CreateCompanyRequest } from "@/model/Company/CreateCompanyRequest";

import { useMutateCreateOrganization } from "@/service/ApplicationMutations";
import { useFetchCompanies } from "@/service/CompanyQueries";
import { useFetchTimezones } from "@/service/Settings/SettingsQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


interface NewOrganizationModalProps {
	open: boolean;
	handleClose: () => void;
}

const NewOrganizationModal: FC<NewOrganizationModalProps> = ({
	open,
	handleClose,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const {
		data: timezones,
		isLoading: isLoadingTimezones,
		isError: isTimezonesError,
	} = useFetchTimezones();
	const { data: companies, isLoading: isLoadingCompanies } =
		useFetchCompanies();
	const createOrganization = useMutateCreateOrganization();

	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	const sectionBoxSx = {
		border: `1px solid ${alpha(
			theme.palette.divider,
			theme.palette.mode === "dark" ? 0.5 : 0.6,
		)}`,
		borderRadius: 2.5,
		p: 2.25,
		bgcolor: theme.palette.background.paper,
		boxShadow:
			theme.palette.mode === "dark"
				? "0 10px 24px rgba(0,0,0,0.16)"
				: "0 8px 24px rgba(31,35,40,0.04)",
	};
	const moduleToggleRowSx = {
		"& .input-container": { width: "100%" },
		"& .MuiFormControlLabel-root": {
			width: "100%",
			m: 0,
			px: 1.5,
			py: 1.25,
			borderRadius: 2,
			border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
			backgroundColor:
				theme.palette.mode === "dark"
					? alpha(theme.palette.common.white, 0.02)
					: alpha(theme.palette.common.black, 0.015),
			justifyContent: "space-between",
			alignItems: "center",
			gap: 2,
		},
		"& .MuiFormControlLabel-labelPlacementStart": {
			width: "100%",
		},
		"& .MuiFormControlLabel-label": {
			flex: 1,
			fontWeight: 500,
			lineHeight: 1.3,
		},
		"& .MuiSwitch-root": {
			mr: 0,
			flexShrink: 0,
		},
	};

	const cleanAndSortTimezones = (timezones: Timezones[]) => {
		const uniqueTimezonesMap = new Map<string, Timezones>();
		timezones.forEach((timezone) => {
			if (!uniqueTimezonesMap.has(timezone.Identifier)) {
				uniqueTimezonesMap.set(timezone.Identifier, timezone);
			}
		});
		const uniqueTimezones = Array.from(uniqueTimezonesMap.values());
		uniqueTimezones.sort((a, b) => {
			const deltaComparison = a.HourDelta.localeCompare(b.HourDelta);
			if (deltaComparison !== 0) return deltaComparison;
			return a.Identifier.localeCompare(b.Identifier);
		});
		return uniqueTimezones;
	};

	const handleSubmit = async (values: CreateCompanyRequest) => {
		try {
			await createOrganization(values);
			handleClose();
			AlertRequest.success();
		} catch (error) {
			console.error(error);
			AlertRequest.failed();
		}
	};

	if (
		isLoadingTimezones ||
		isLoadingCompanies ||
		isTimezonesError ||
		!companies ||
		!timezones
	) {
		return null;
	}

	const sortedTimezones = cleanAndSortTimezones(timezones);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="lg"
			aria-labelledby="new-organization-title"
			PaperProps={{
				sx: {
					borderRadius: 2.5,
					border: "1px solid",
					borderColor: "divider",
					overflow: "hidden",
					bgcolor: "background.paper",
					boxShadow:
						theme.palette.mode === "dark"
							? "0 24px 64px rgba(0,0,0,0.42)"
							: "0 28px 80px rgba(31,35,40,0.18)",
				},
			}}
		>
			<DialogTitle
				id="new-organization-title"
				component="div"
				sx={{
					position: "relative",
					px: { xs: 2, sm: 3 },
					pt: 2.25,
					pb: 2,
					borderBottom: 1,
					borderColor: "divider",
					bgcolor: "background.paper",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, pr: 5 }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 38,
							height: 38,
							borderRadius: 1.5,
							bgcolor: alpha(theme.palette.primary.main, 0.1),
							color: "primary.main",
							flexShrink: 0,
						}}
					>
						<DomainAddOutlinedIcon fontSize="small" />
					</Box>
					<Typography
						variant="h6"
						component="h2"
						sx={{ fontWeight: 700, color: "text.primary" }}
					>
						<Translate String="Settings.NewOrganization.Title">
							Create New Organisation
						</Translate>
					</Typography>
				</Box>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Components.Admin.AdminDashboard.Components.NewOrganisationModal.L207C17.Close", "Close")}
					onClick={handleClose}
					size="small"
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						...closeButtonSx,
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent
				sx={{
					px: { xs: 2, sm: 3 },
					pt: 2.5,
					pb: 0,
					bgcolor: "background.paper",
				}}
			>
				<Formik
					initialValues={{
						Name: "",
						ExternalId: "",
						Language: "en",
						Timezone: "Etc/UTC",
						Modules: {
							IsCompetenceActive: true,
							IsLabActive: true,
							IsMaterialHandlingActive: true,
							IsProceduresActive: true,
							IsShiftActive: true,
							IsTaskActive: true,
						},
						ActiveDirectoryGroups: {
							Guest: "",
							Standard: "",
							Supervisor: "",
							Administrator: "",
						},
					}}
					onSubmit={handleSubmit}
					validationSchema={() => NewOrganisationModalValidation(companies)}
				>
					{({ values }) => (
						<Form>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: {
										xs: "1fr",
										md: "minmax(0, 1.05fr) minmax(0, 1fr)",
									},
									gap: 3,
								}}
							>
								{/* Left column: Organisation details */}
								<Box
									sx={{
										flex: 1,
										display: "flex",
										flexDirection: "column",
										gap: 2,
									}}
								>
										<CustomInput
											label={GetLanguageValueAsString(
												translation,
												"Settings.NewOrganization.Name",
												"Organisation name",
											)}
											name="Name"
										type="text"
										id="organisationName"
										translationString="Settings.NewOrganization.Name"
									/>
										<CustomInput
											label={GetLanguageValueAsString(
												translation,
												"Settings.NewOrganization.ExternalId",
												"External Id (for API usage)",
											)}
											name="ExternalId"
										type="text"
										id="externalId"
										translationString="Settings.NewOrganization.ExternalId"
									/>
										<CustomSelect
											style={{ maxHeight: "40px" }}
											label={GetLanguageValueAsString(
												translation,
												"Settings.NewOrganization.Language",
												"Language",
											)}
										name="Language"
										id="language"
											translationString="Settings.NewOrganization.Language"
											defaultValue={values.Language}
										>
											<option value="en">
												<Translate String="Common.Language.English">
													English
												</Translate>
											</option>
											<option value="no">
												<Translate String="Common.Language.Norwegian">
													Norwegian
												</Translate>
											</option>
											<option value="pt">
												<Translate String="Common.Language.Portuguese">
													Portuguese
												</Translate>
											</option>
										</CustomSelect>
										<CustomSelect
											style={{ maxHeight: "40px" }}
											label={GetLanguageValueAsString(
												translation,
												"Settings.NewOrganization.Timezone",
												"Timezone",
											)}
										name="Timezone"
										id="timezone"
										translationString="Settings.NewOrganization.Timezone"
										defaultValue={values.Timezone}
									>
										{sortedTimezones.map((x) => (
											<option key={x.Identifier} value={x.Identifier}>
												{x.Identifier} ({x.HourDelta.trim()})
											</option>
										))}
									</CustomSelect>

									<Typography
										variant="subtitle2"
										sx={{
											display: "block",
											color: "text.secondary",
											fontWeight: 600,
											letterSpacing: "0.01em",
										}}
									>
										<Translate String="Settings.NewOrganization.Modules">
											Modules
										</Translate>
									</Typography>
									<Box sx={sectionBoxSx}>
										<Grid container spacing={2}>
											<Grid item xs={12} md={6}>
												<Box sx={moduleToggleRowSx}>
														<CustomCheckbox
															label={GetLanguageValueAsString(
																translation,
																"SideBar.Procedure",
																"Procedures",
															)}
														name="Modules.IsProceduresActive"
														id="procedures"
														type="checkbox"
														asSwitch
													/>
												</Box>
											</Grid>
											<Grid item xs={12} md={6}>
												<Box sx={moduleToggleRowSx}>
														<CustomCheckbox
															label={GetLanguageValueAsString(
																translation,
																"Breadcrumbs.Link.Competency",
																"Competence",
															)}
														name="Modules.IsCompetenceActive"
														id="competence"
														type="checkbox"
														asSwitch
													/>
												</Box>
											</Grid>
											<Grid item xs={12} md={6}>
												<Box sx={moduleToggleRowSx}>
														<CustomCheckbox
															label={GetLanguageValueAsString(
																translation,
																"SideBar.Shift",
																"Shift",
															)}
														name="Modules.IsShiftActive"
														id="shift"
														type="checkbox"
														asSwitch
													/>
												</Box>
											</Grid>
											<Grid item xs={12} md={6}>
												<Box sx={moduleToggleRowSx}>
														<CustomCheckbox
															label={GetLanguageValueAsString(
																translation,
																"SideBar.Task",
																"Task",
															)}
														name="Modules.IsTaskActive"
														id="task"
														type="checkbox"
														asSwitch
													/>
												</Box>
											</Grid>
											<Grid item xs={12} md={6}>
												<Box sx={moduleToggleRowSx}>
														<CustomCheckbox
															label={GetLanguageValueAsString(
																translation,
																"SideBar.Lab",
																"Lab",
															)}
														name="Modules.IsLabActive"
														id="lab"
														type="checkbox"
														asSwitch
													/>
												</Box>
											</Grid>
											<Grid item xs={12} md={6}>
												<Box sx={moduleToggleRowSx}>
														<CustomCheckbox
															label={GetLanguageValueAsString(
																translation,
																"MaterialHandling.Title",
																"Material Handling",
															)}
														name="Modules.IsMaterialHandlingActive"
														id="materialHandling"
														type="checkbox"
														asSwitch
													/>
												</Box>
											</Grid>
										</Grid>
									</Box>
								</Box>

								{/* Right column: Active Directory Groups */}
								<Box
									sx={{
										flex: 1,
										display: "flex",
										flexDirection: "column",
										gap: 2,
									}}
								>
									<Typography
										variant="subtitle2"
										sx={{
											display: "block",
											color: "text.secondary",
											fontWeight: 600,
											letterSpacing: "0.01em",
										}}
									>
										<Translate String="Settings.NewOrganization.ActiveDirectoryGroups">
											Active Directory Groups
										</Translate>
									</Typography>
									<Box sx={sectionBoxSx}>
											<CustomInput
												type="text"
												id="guest"
												name="ActiveDirectoryGroups.Guest"
												label={GetLanguageValueAsString(
													translation,
													"AD.group.guest",
													"Guest",
												)}
											translationString="AD.group.guest"
										/>
											<CustomInput
												type="text"
												id="standard"
												name="ActiveDirectoryGroups.Standard"
												label={GetLanguageValueAsString(
													translation,
													"AD.group.standard",
													"Standard",
												)}
											translationString="AD.group.standard"
										/>
											<CustomInput
												type="text"
												id="supervisor"
												name="ActiveDirectoryGroups.Supervisor"
												label={GetLanguageValueAsString(
													translation,
													"AD.group.supervisor",
													"Supervisor",
												)}
											translationString="AD.group.supervisor"
										/>
											<CustomInput
												type="text"
												id="administrator"
												name="ActiveDirectoryGroups.Administrator"
												label={GetLanguageValueAsString(
													translation,
													"AD.group.administrator",
													"Administrator",
												)}
											translationString="AD.group.administrator"
										/>
									</Box>
								</Box>
							</Box>

							<DialogActions
								sx={{
									px: 0,
									py: 2.25,
									mt: 2.5,
									borderTop: 1,
									borderColor: "divider",
									display: "flex",
									justifyContent: "flex-end",
									gap: 1.5,
									flexWrap: "wrap",
								}}
							>
								<Button
									variant="outlined"
									color="inherit"
									startIcon={<CloseIcon />}
									onClick={handleClose}
									sx={{
										height: 38,
										px: 1.75,
										textTransform: "none",
										fontWeight: 600,
										borderColor: "divider",
										bgcolor: "background.paper",
										boxShadow: "none",
									}}
								>
									<Translate String="Procedure.Button.Close">Close</Translate>
								</Button>
								<ProcedureButton
									intent="success"
									startIcon={<SaveIcon />}
									type="submit"
									sx={{
										height: 38,
										px: 1.9,
										boxShadow: "none",
									}}
								>
									<Translate String="Procedure.Button.Create">Create</Translate>
								</ProcedureButton>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default NewOrganizationModal;
